import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ignoreUndoRedoHotkey as shouldIgnoreMotionEditorHotkey } from '../utils';
import {
  CLOSED_WORLD_ADD_MENU,
  WORLD_ADD_MENU_HEIGHT,
  WORLD_ADD_MENU_PADDING,
  WORLD_ADD_MENU_WIDTH,
} from './constants';
import {
  createMotionEditorWorldAsset,
  createMotionEditorWorldPrimitive,
  revokeMotionEditorWorldObjectAssets,
} from './objectFactory';
import type {
  MotionEditorWorldAddMenuState,
  MotionEditorWorldAssetFormat,
  MotionEditorWorldObject,
  MotionEditorWorldObjectTransform,
  MotionEditorWorldPointer,
  MotionEditorWorldPrimitiveType,
  MotionEditorWorldTransformMode,
} from './types';

interface UseMotionEditorWorldObjectsOptions {
  stageRef: { current: HTMLDivElement | null };
}

export function useMotionEditorWorldObjects({
  stageRef,
}: UseMotionEditorWorldObjectsOptions) {
  const worldPointerRef = useRef<MotionEditorWorldPointer | null>(null);
  const worldObjectsRef = useRef<readonly MotionEditorWorldObject[]>([]);
  const assetInputRef = useRef<HTMLInputElement | null>(null);
  const pendingAssetFormatRef = useRef<MotionEditorWorldAssetFormat>('glb');
  const pendingAssetPointRef = useRef<MotionEditorWorldPointer['point']>([
    0, 0, 0,
  ]);
  const [objects, setObjects] = useState<MotionEditorWorldObject[]>([]);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [hoveredObjectId, setHoveredObjectId] = useState<string | null>(null);
  const [transformMode, setTransformMode] =
    useState<MotionEditorWorldTransformMode>('translate');
  const [addMenu, setAddMenu] = useState<MotionEditorWorldAddMenuState>(
    CLOSED_WORLD_ADD_MENU,
  );

  useEffect(() => {
    worldObjectsRef.current = objects;
  }, [objects]);

  useEffect(
    () => () => {
      revokeMotionEditorWorldObjectAssets(worldObjectsRef.current);
    },
    [],
  );

  const handlePointerUpdate = useCallback(
    (pointer: MotionEditorWorldPointer) => {
      worldPointerRef.current = pointer;
    },
    [],
  );

  const clearSelection = useCallback(() => {
    setSelectedObjectId(null);
  }, []);

  const closeAddMenu = useCallback(() => {
    setAddMenu((current) =>
      current.open ? { ...current, open: false } : current,
    );
  }, []);

  const openAddMenu = useCallback(() => {
    const rect = stageRef.current?.getBoundingClientRect();
    const pointer = worldPointerRef.current;
    const fallbackClientX = rect ? rect.left + rect.width * 0.5 : 0;
    const fallbackClientY = rect ? rect.top + rect.height * 0.5 : 0;
    const clientX = pointer?.clientX ?? fallbackClientX;
    const clientY = pointer?.clientY ?? fallbackClientY;
    const position = rect
      ? getClampedWorldAddMenuPosition(clientX, clientY, rect)
      : { x: 0, y: 0 };

    setAddMenu({
      open: true,
      x: position.x,
      y: position.y,
      point: pointer?.point ?? [0, 0, 0],
    });
  }, [stageRef]);

  const handleTransformObject = useCallback(
    (objectId: string, transform: MotionEditorWorldObjectTransform) => {
      setObjects((current) =>
        current.map((object) =>
          object.id === objectId ? { ...object, transform } : object,
        ),
      );
    },
    [],
  );

  const handleAddPrimitive = useCallback(
    (type: MotionEditorWorldPrimitiveType) => {
      setObjects((current) => {
        const object = createMotionEditorWorldPrimitive(
          type,
          addMenu.point,
          current,
        );
        setSelectedObjectId(object.id);
        setTransformMode('translate');
        return [...current, object];
      });
      closeAddMenu();
    },
    [addMenu.point, closeAddMenu],
  );

  const handleImportAsset = useCallback(
    (format: MotionEditorWorldAssetFormat) => {
      pendingAssetFormatRef.current = format;
      pendingAssetPointRef.current = addMenu.point;
      closeAddMenu();
      if (assetInputRef.current) {
        assetInputRef.current.accept = format === 'glb' ? '.glb' : '.obj';
        assetInputRef.current.click();
      }
    },
    [addMenu.point, closeAddMenu],
  );

  const handleAssetInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.currentTarget.files?.[0] ?? null;
      event.currentTarget.value = '';
      if (!file) return;

      const suffix = file.name.split('.').pop()?.toLowerCase();
      const format: MotionEditorWorldAssetFormat =
        suffix === 'glb' || suffix === 'obj'
          ? suffix
          : pendingAssetFormatRef.current;

      setObjects((current) => {
        const object = createMotionEditorWorldAsset(
          file,
          format,
          pendingAssetPointRef.current,
          current,
        );
        setSelectedObjectId(object.id);
        setTransformMode('translate');
        return [...current, object];
      });
    },
    [],
  );

  const deleteSelectedObject = useCallback(() => {
    const objectId = selectedObjectId;
    if (!objectId) return;

    setObjects((current) => {
      const removed = current.filter((object) => object.id === objectId);
      revokeMotionEditorWorldObjectAssets(removed);
      return current.filter((object) => object.id !== objectId);
    });
    setHoveredObjectId((current) => (current === objectId ? null : current));
    setSelectedObjectId(null);
  }, [selectedObjectId]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreMotionEditorHotkey(event) || event.repeat) {
        return;
      }

      if (
        event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        event.code === 'KeyA'
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        openAddMenu();
        return;
      }

      if (event.code === 'Escape') {
        if (!addMenu.open && !selectedObjectId) return;
        event.preventDefault();
        closeAddMenu();
        clearSelection();
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }

      if (event.code === 'KeyQ' && selectedObjectId) {
        event.preventDefault();
        clearSelection();
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey || !selectedObjectId) {
        return;
      }

      if (event.code === 'KeyW') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setTransformMode('translate');
      } else if (event.code === 'KeyE') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setTransformMode('rotate');
      } else if (event.code === 'KeyR') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setTransformMode('scale');
      } else if (event.code === 'Delete' || event.code === 'Backspace') {
        event.preventDefault();
        event.stopImmediatePropagation();
        deleteSelectedObject();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    addMenu.open,
    clearSelection,
    closeAddMenu,
    deleteSelectedObject,
    openAddMenu,
    selectedObjectId,
  ]);

  return {
    layerProps: {
      hoveredObjectId,
      objects,
      onHoverObject: setHoveredObjectId,
      onPointerUpdate: handlePointerUpdate,
      onSelectObject: setSelectedObjectId,
      onTransformObject: handleTransformObject,
      selectedObjectId,
      transformMode,
    },
    controlsProps: {
      assetInputRef,
      menu: addMenu,
      onAddPrimitive: handleAddPrimitive,
      onAssetInputChange: handleAssetInputChange,
      onCloseAddMenu: closeAddMenu,
      onImportAsset: handleImportAsset,
    },
  };
}

function getClampedWorldAddMenuPosition(
  clientX: number,
  clientY: number,
  rect: DOMRect,
) {
  const maxX = Math.max(
    WORLD_ADD_MENU_PADDING,
    rect.width - WORLD_ADD_MENU_WIDTH,
  );
  const maxY = Math.max(
    WORLD_ADD_MENU_PADDING,
    rect.height - WORLD_ADD_MENU_HEIGHT,
  );

  return {
    x: Math.min(Math.max(clientX - rect.left, WORLD_ADD_MENU_PADDING), maxX),
    y: Math.min(Math.max(clientY - rect.top, WORLD_ADD_MENU_PADDING), maxY),
  };
}
